import i18n from "@/lib/i18n";

/**
 * Returns a human-readable, locale-aware relative time string from an ISO 8601
 * datetime string, using `Intl.RelativeTimeFormat` with the active i18n locale.
 *
 * Ranges:
 *   < 60 s   → "just now" / "только что"
 *   < 60 min → "N minutes ago" / "N минут назад"
 *   < 24 h   → "N hours ago" / "N часов назад"
 *   else     → "N days ago" / "N дней назад"
 *
 * @param isoString ISO 8601 datetime string.
 * @param locale    Optional BCP-47 locale; defaults to the active i18n language.
 */
export function formatRelativeTime(isoString: string, locale?: string): string {
  const resolvedLocale = locale ?? i18n.language;
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffSec = Math.floor(diffMs / 1000);

  const rtf = new Intl.RelativeTimeFormat(resolvedLocale, { numeric: "auto" });

  if (diffSec < 60) {
    // "now" with numeric:"auto" yields the idiomatic phrase ("just now" / "сейчас").
    return rtf.format(0, "second");
  }

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return rtf.format(-diffMin, "minute");
  }

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) {
    return rtf.format(-diffHr, "hour");
  }

  const diffDays = Math.floor(diffHr / 24);
  return rtf.format(-diffDays, "day");
}
